import { useEffect, useCallback } from "react";

import { toast } from "@/components/ui/sonner";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";

const MILESTONE_MESSAGES: Record<string, string> = {
  'first_day': '🎉 You started your activity streak!',
  'streak_7': '🔥 7-day streak! You earned the "1 Week Warrior" badge!',
  'streak_14': '⚡ 14-day streak! You earned the "2 Week Champion" badge!',
  'streak_30': '🏆 30-day streak! You earned the "Monthly Master" badge!',
  'streak_60': '💎 60-day streak! You earned the "2 Month Legend" badge!',
  'streak_100': '👑 100-day streak! You earned the "100 Day Titan" badge!',
  'streak_365': '🌟 365-day streak! You earned the "Year-Long Hero" badge!',
};

export const useActivityTracking = () => {
  const { user } = useAuth();

  const updateStreak = useCallback(async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase.rpc('update_user_streak', {
        p_user_id: user.id,
      });

      if (error) {
        console.error('Error updating streak:', error);
        return;
      }

      const result = data as any;

      // Show milestone achievement toast
      if (result?.is_new_milestone && result?.milestone_type) {
        const message = MILESTONE_MESSAGES[result.milestone_type];
        if (message) {
          toast.success(message, {
            duration: 5000,
          });
        }
      }

      // Show streak broken notification (but not on first day)
      if (result?.streak_broken && result?.current_streak === 1 && result?.longest_streak > 1) {
        toast.error('Your streak was broken! Start a new one today.', {
          duration: 4000,
        });
      }
    } catch (error) {
      console.error('Failed to update activity streak:', error);
    }
  }, [user]);

  // Update streak once when component mounts (if user has activity today)
  useEffect(() => {
    if (!user) return;

    // Check if we've already updated streak today
    const lastUpdate = localStorage.getItem(`streak_updated_${user.id}`);
    const today = new Date().toDateString();

    if (lastUpdate !== today) {
      updateStreak();
      localStorage.setItem(`streak_updated_${user.id}`, today);
    }
  }, [user, updateStreak]);

  return { updateStreak };
};
